import { z } from 'zod';
import {
  createAuthAPI,
  createPublicAPI,
} from '../utils/fetch/create-api-factory';
import { type JobRequestPublic, JobResponsePublicSchema } from './job.schema';
import {
  type JobRequest,
  JobResponseSchema,
  JobSchema,
  JobsResponseSchema,
  ReasonsSchema,
} from './job.schema';

export const getJobs = (page: number, token: string) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs?page=${page}`,
    method: 'GET',
    schema: JobsResponseSchema,
  })({ token });

export const getJob = (jobId: string, token: string) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}`,
    method: 'GET',
    schema: JobSchema,
  })({ token });

export const postJob = createAuthAPI<JobRequest, typeof JobResponseSchema>({
  url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs`,
  method: 'POST',
  schema: JobResponseSchema,
});

export const postJobPublic = createPublicAPI<
  JobRequestPublic,
  typeof JobResponsePublicSchema
>({
  url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/jobs`,
  method: 'POST',
  schema: JobResponsePublicSchema,
});

export const postTradesToJob = ({
  jobId,
  ...rest
}: {
  jobId: string;
  payload: {
    trades: number[];
  };
  token: string;
}) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/trades`,
    method: 'POST',
    schema: JobSchema,
  })({ ...rest });

export const postTradesToJobPublic = ({
  jobId,
  ...rest
}: {
  jobId: string;
  payload: {
    email: string;
    trades: number[];
  };
}) =>
  createPublicAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_PUBLIC_URL}/jobs/${jobId}/trades`,
    method: 'POST',
    schema: JobSchema,
  })({ ...rest });

export const getJobLeads = (jobId: string, token: string) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/trades`,
    method: 'GET',
    schema: JobSchema,
  })({ token });

export const getJobCloseReasons = (jobId: string, payload: { token: string }) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/cancel-reasons`,
    schema: ReasonsSchema,
  })(payload);

export const cancelJob = (
  jobId: string,
  token: string,
  payload: {
    reason: {
      id: string;
      customReason?: string;
    };
  },
) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/cancel`,
    schema: JobSchema,
    method: 'POST',
  })({ token, payload });

export const closeJob = (
  jobId: string,
  token: string,
  payload: {
    reason: {
      id: string;
      customReason?: string;
    };
  },
) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/close`,
    schema: JobSchema,
    method: 'POST',
  })({
    token,
    payload: {
      reason: payload.reason.id,
      reasonDetails: payload.reason.customReason,
    },
  });

export const bookJob = (
  jobId: string,
  token: string,
  payload: {
    opportunityId: string;
  },
) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/book`,
    schema: JobSchema,
    method: 'POST',
  })({ token, payload });

export const completeJob = (
  jobId: string,
  token: string,
  payload: {
    opportunityId: string;
    cost?: number;
  },
) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/work-finished`,
    schema: JobSchema,
    method: 'POST',
  })({ token, payload });

export const closeJobOpportunity = ({
  jobId,
  opportunityId,
  ...rest
}: {
  jobId: string;
  opportunityId: string;
  payload: {
    reason: {
      id: string;
      customReason?: string;
    };
  };
  token: string;
}) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/opportunities/${opportunityId}/cancel`,
    method: 'POST',
    schema: JobSchema,
  })({ ...rest });

export const getJobOpportunityCloseReasons = createAuthAPI({
  url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/opportunity/cancel-reasons`,
  schema: z.any(),
});

export const shareAddressWithTrade = ({
  jobId,
  opportunityId,
  token,
}: {
  jobId: string;
  opportunityId: string;
  token: string;
}) =>
  createAuthAPI({
    url: `${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_URL}/jobs/${jobId}/opportunities/${opportunityId}/share-address`,
    method: 'POST',
    schema: z.object({}),
  })({ token });
